import { lazy, Suspense } from 'react';

import { createBrowserRouter, Navigate } from 'react-router-dom';

import ErrorFallback from 'components/error-fallback';
import FetchingSpinner from 'components/fetching-spinner';
import TermsOfUse from 'components/terms-of-use';
import TermsOfUseIntegrators from 'components/terms-of-use/integrators';
import env from 'configs/env';
import DataViewerLayout from 'pages/data-viewer';

import ExternalRoute from './ExternalRoute';
import Nonceroute from './NonceRoute';

const TermsOfUseStandalone = lazy(() => import('components/terms-of-use-standalone'));
const AdminRoute = lazy(() => import('./AdminRoute'));
const PhysicianRoute = lazy(() => import('./PhysicianRoute'));
const PrivateRoute = lazy(() => import('./PrivateRoute'));
const PublicRoute = lazy(() => import('./PublicRoute'));
const VerifiedRoute = lazy(() => import('./VerifiedRoute'));

const AuthLayout = lazy(() => import('pages/auth/layout'));
const Login = lazy(() => import('pages/auth/views/login/Login'));
const TwoFACheck = lazy(() => import('pages/auth/views/login/TwoFACheck'));
const ResetPassword = lazy(() => import('pages/auth/views/reset-password/ResetPassword'));
const ActivateEnrollment = lazy(
  () => import('pages/auth/views/activate-enrollment/ActivateEnrollment')
);
const TwoFactor = lazy(() => import('pages/auth/views/two-factor/TwoFactor'));
const ForgotPassword = lazy(() => import('pages/auth/views/forgot-password/ForgotPassword'));

const Dashboardlayout = lazy(() => import('pages/dashboard/layout/Dashboardlayout'));
const Studies = lazy(() => import('pages/dashboard/pages/studies'));
const AllCenters = lazy(() => import('pages/dashboard/pages/centers'));
const DevCenters = lazy(() => import('pages/dashboard/pages/developer-center'));

const APIClient = lazy(() => import('pages/api-client'));

const Profilelayout = lazy(() => import('pages/dashboard/pages/profile/layout/Profilelayout'));
const Centers = lazy(() => import('pages/dashboard/pages/profile/views/centers'));
const Security = lazy(() => import('pages/dashboard/pages/profile/views/security'));
const MyProfile = lazy(() => import('pages/dashboard/pages/profile/views/my-profile'));

const Studylayout = lazy(() => import('pages/study-viewer/layout/Studylayout'));
const AllNight = lazy(() => import('pages/study-viewer/views/all-night'));
const NightN = lazy(() => import('pages/study-viewer/views/night-n'));
const Survey = lazy(() => import('pages/study-viewer/views/survey'));
const Interpretation = lazy(() => import('pages/study-viewer/views/interpretation'));
const EventLog = lazy(() => import('pages/study-viewer/views/event-log'));
const StudySettings = lazy(() => import('pages/study-viewer/views/study-settings'));
const Notes = lazy(() => import('pages/study-viewer/views/notes'));
const Files = lazy(() => import('pages/study-viewer/views/files'));

const CenterLayout = lazy(() => import('pages/dashboard/pages/center/layout/Centerlayout'));
const Members = lazy(() => import('pages/dashboard/pages/center/views/members'));
const CenterDetails = lazy(() => import('pages/dashboard/pages/center/views/center-details'));
const CenterSecurity = lazy(() => import('pages/dashboard/pages/center/views/security'));
const IPSnippets = lazy(() => import('pages/dashboard/pages/center/views/ip-snippets'));
const ChangeLog = lazy(() => import('pages/dashboard/pages/center/views/change-log'));

const Instruction = lazy(() => import('pages/instruction'));

const handleReroute = () => {
  window.location.pathname = '';
};

export const router = createBrowserRouter(
  [
    {
      path: '',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <PrivateRoute>
            <Suspense fallback={<FetchingSpinner />}>
              <Dashboardlayout />
            </Suspense>
          </PrivateRoute>
        </Suspense>
      ),
      errorElement: (
        <ErrorFallback
          actionText="Go back to home page"
          errorText="Something went wrong"
          actionHandler={handleReroute}
        />
      ),
      children: [
        {
          path: 'studies',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <PhysicianRoute>
                <Suspense fallback={<FetchingSpinner />}>
                  <Studies />
                </Suspense>
              </PhysicianRoute>
            </Suspense>
          ),
        },
        {
          path: 'profile',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <Profilelayout />
            </Suspense>
          ),
          children: [
            {
              path: 'centers',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <VerifiedRoute>
                    <Suspense fallback={<FetchingSpinner />}>
                      <Centers />
                    </Suspense>
                  </VerifiedRoute>
                </Suspense>
              ),
            },
            {
              path: 'security',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <Security />
                </Suspense>
              ),
            },
            {
              path: 'my',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <MyProfile />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'center/:key',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <AdminRoute>
                <Suspense fallback={<FetchingSpinner />}>
                  <CenterLayout />
                </Suspense>
              </AdminRoute>
            </Suspense>
          ),
          children: [
            {
              path: 'enrollments',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <Members />
                </Suspense>
              ),
            },
            {
              path: 'details',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <CenterDetails />
                </Suspense>
              ),
            },
            {
              path: 'security',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <CenterSecurity />
                </Suspense>
              ),
            },
            {
              path: 'ip-snippets',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <IPSnippets />
                </Suspense>
              ),
            },
            {
              path: 'change-log',
              element: (
                <Suspense fallback={<FetchingSpinner />}>
                  <ChangeLog />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: 'centers',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <AdminRoute>
                <Suspense fallback={<FetchingSpinner />}>
                  <AllCenters />
                </Suspense>
              </AdminRoute>
            </Suspense>
          ),
        },
        {
          path: 'developer',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <AdminRoute>
                <Suspense fallback={<FetchingSpinner />}>
                  <DevCenters />
                </Suspense>
              </AdminRoute>
            </Suspense>
          ),
        },
        {
          path: '',
          element: <Navigate replace to="/studies" />,
        },
      ],
    },
    {
      path: 'dashboard/charting',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <ExternalRoute>
            <Suspense fallback={<FetchingSpinner />}>
              <DataViewerLayout />
            </Suspense>
          </ExternalRoute>
        </Suspense>
      ),
    },
    {
      path: 'termsofuse',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <Nonceroute>
            <Suspense fallback={<FetchingSpinner />}>
              <TermsOfUseIntegrators />
            </Suspense>
          </Nonceroute>
        </Suspense>
      ),
    },
    {
      path: 'termsofuse-hcp',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <ExternalRoute>
            <Suspense fallback={<FetchingSpinner />}>
              <TermsOfUseIntegrators />
            </Suspense>
          </ExternalRoute>
        </Suspense>
      ),
    },
    {
      path: 'check2fa',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <PrivateRoute>
            <Suspense fallback={<FetchingSpinner />}>
              <TwoFACheck />
            </Suspense>
          </PrivateRoute>
        </Suspense>
      ),
    },
    {
      path: 'study/:key',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <PrivateRoute>
            <Suspense fallback={<FetchingSpinner />}>
              <Studylayout />
            </Suspense>
          </PrivateRoute>
        </Suspense>
      ),
      errorElement: (
        <ErrorFallback
          actionText="Go back to home page"
          errorText="Something went wrong"
          actionHandler={handleReroute}
        />
      ),
      children: [
        {
          path: 'all-night',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <AllNight />
            </Suspense>
          ),
        },
        {
          path: 'night/:recording',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <NightN />
            </Suspense>
          ),
        },
        {
          path: 'surveys',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <Survey />
            </Suspense>
          ),
        },
        {
          path: 'interpretation',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <Interpretation />
            </Suspense>
          ),
        },
        {
          path: 'event-log',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <EventLog />
            </Suspense>
          ),
        },
        {
          path: 'study-settings',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <StudySettings />
            </Suspense>
          ),
        },
        {
          path: 'notes',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <Notes />
            </Suspense>
          ),
        },
        {
          path: 'files',
          element: (
            <Suspense fallback={<FetchingSpinner />}>
              <Files />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'termsofuse-static',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <PublicRoute>
            <Suspense fallback={<FetchingSpinner />}>
              <TermsOfUseStandalone />
            </Suspense>
          </PublicRoute>
        </Suspense>
      ),
      errorElement: (
        <ErrorFallback
          actionText="Go back to home page"
          errorText="Something went wrong"
          actionHandler={handleReroute}
        />
      ),
    },
    {
      path: 'signin',
      errorElement: (
        <ErrorFallback
          actionText="Go back to home page"
          errorText="Something went wrong"
          actionHandler={handleReroute}
        />
      ),
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <PublicRoute>
            <Suspense fallback={<FetchingSpinner />}>
              <AuthLayout />
            </Suspense>
          </PublicRoute>
        </Suspense>
      ),
      children: [
        {
          path: '',
          element: (
            <Suspense fallback={<></>}>
              <Login />
            </Suspense>
          ),
        },
        {
          path: 'service',
          element: (
            <Suspense fallback={<></>}>
              <Login />
            </Suspense>
          ),
        },
        {
          path: 'two-factor',
          element: (
            <Suspense fallback={<></>}>
              <TwoFactor />
            </Suspense>
          ),
        },
        {
          path: 'terms',
          element: (
            <Suspense fallback={<></>}>
              <TermsOfUse />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'enrollments',
      errorElement: (
        <ErrorFallback
          actionText="Go back to home page"
          errorText="Something went wrong"
          actionHandler={handleReroute}
        />
      ),
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <AuthLayout />
        </Suspense>
      ),
      children: [
        {
          path: 'activate',
          element: (
            <Suspense fallback={<></>}>
              <ActivateEnrollment />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'forgot_password',
      errorElement: (
        <ErrorFallback
          actionText="Go back to home page"
          errorText="Something went wrong"
          actionHandler={handleReroute}
        />
      ),
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <AuthLayout />
        </Suspense>
      ),
      children: [
        {
          path: '',
          element: (
            <Suspense fallback={<></>}>
              <ForgotPassword />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'reset',
      errorElement: (
        <ErrorFallback
          actionText="Go back to home page"
          errorText="Something went wrong"
          actionHandler={handleReroute}
        />
      ),
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <AuthLayout />
        </Suspense>
      ),
      children: [
        {
          path: 'password',
          element: (
            <Suspense fallback={<></>}>
              <ResetPassword />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'activation-instructions',
      element: (
        <Suspense fallback={<FetchingSpinner />}>
          <Instruction />
        </Suspense>
      ),
      errorElement: <ErrorFallback errorText="Something went wrong" />,
    },
    {
      path: 'api-client',
      element:
        env.mode === 'development' || env.environment !== 'prod' ? (
          <Suspense fallback={<FetchingSpinner />}>
            <APIClient />
          </Suspense>
        ) : (
          <ErrorFallback
            actionText="Go back to home page"
            errorText="Page not found"
            actionHandler={handleReroute}
          />
        ),
    },
  ],
  { basename: '' }
);
